// =============================================================================
// features/sales/campaigns/wizard/components/AudienceFilterFields.tsx —
// Critérios do filtro do CRM (etapa 2, fonte "filter"). Extraído de
// Step2Audience.tsx para manter o orquestrador de etapa abaixo de 200 linhas
// (norma anti-padrão do DS).
// =============================================================================

import * as React from 'react';

import { Input } from '../../../../../components/ui/Input';
import { useAssignableAgents } from '../../../../../hooks/admin/useAgents';
import { cn } from '../../../../../lib/cn';
import { LEAD_STATUS_OPTIONS } from '../labels';
import type { AudienceFilterState } from '../types';

export interface AudienceFilterFieldsProps {
  value: AudienceFilterState;
  onChange: (value: AudienceFilterState) => void;
}

export function AudienceFilterFields({
  value,
  onChange,
}: AudienceFilterFieldsProps): React.JSX.Element {
  const { agents } = useAssignableAgents();

  function toggleLeadStatus(status: (typeof LEAD_STATUS_OPTIONS)[number]['value']): void {
    const current = new Set(value.leadStatus ?? []);
    if (current.has(status)) current.delete(status);
    else current.add(status);
    const arr = Array.from(current);
    onChange({ ...value, leadStatus: arr.length > 0 ? arr : undefined });
  }

  function toggleAgent(agentId: string): void {
    const current = new Set(value.assignedAgentIds ?? []);
    if (current.has(agentId)) current.delete(agentId);
    else current.add(agentId);
    const arr = Array.from(current);
    onChange({ ...value, assignedAgentIds: arr.length > 0 ? arr : undefined });
  }

  return (
    <div className="flex flex-col gap-5">
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-semibold text-ink mb-1">Status do lead</legend>
        <div className="flex flex-wrap gap-2">
          {LEAD_STATUS_OPTIONS.map((opt) => {
            const checked = (value.leadStatus ?? []).includes(opt.value);
            return (
              <label
                key={opt.value}
                className={cn(
                  'inline-flex items-center gap-2 px-3 h-[40px] rounded-sm border cursor-pointer select-none',
                  'font-sans text-sm text-ink transition-colors duration-fast',
                  checked
                    ? 'border-azul bg-[rgba(27,58,140,0.06)]'
                    : 'border-border-strong hover:bg-surface-hover',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleLeadStatus(opt.value)}
                  className="w-4 h-4 accent-azul"
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      </fieldset>

      {agents.length > 0 && (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold text-ink mb-1">Responsável atual do lead</legend>
          <div className="flex flex-wrap gap-2">
            {agents.map((a) => {
              const checked = (value.assignedAgentIds ?? []).includes(a.id);
              return (
                <label
                  key={a.id}
                  className={cn(
                    'inline-flex items-center gap-2 px-3 h-[40px] rounded-sm border cursor-pointer select-none',
                    'font-sans text-sm text-ink transition-colors duration-fast',
                    checked
                      ? 'border-azul bg-[rgba(27,58,140,0.06)]'
                      : 'border-border-strong hover:bg-surface-hover',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAgent(a.id)}
                    className="w-4 h-4 accent-azul"
                  />
                  {a.display_name}
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          id="wizard-audience-created-from"
          type="date"
          label="Criado de"
          value={value.createdFrom}
          onChange={(e) => onChange({ ...value, createdFrom: e.target.value })}
        />
        <Input
          id="wizard-audience-created-to"
          type="date"
          label="Criado até"
          value={value.createdTo}
          onChange={(e) => onChange({ ...value, createdTo: e.target.value })}
        />
      </div>
    </div>
  );
}
